import { useState, type ReactNode } from 'react';
import {
  Callout,
  Field,
  Modal,
  TextInput,
  explainFailure,
} from '@affdice/portal-kit';

import { t } from '../messages.ts';

/**
 * One dialog for every direct edit (Rule Book 3.9d): the form's own fields,
 * then the reason the change is recorded with, a line saying what pressing
 * the button does (the change lands now, under the viewer's name, and the
 * operator is told), and the button.
 *
 * The reason is required: the server refuses a direct edit without one, so
 * the button stays off until there is one. A refusal shows the server's own
 * words where the form is, and the form keeps what was typed.
 */
export function DirectEditDialog({
  title,
  submitLabel,
  danger = false,
  ready,
  run,
  onClose,
  onDone,
  wide = false,
  children,
}: {
  title: string;
  submitLabel: string;
  /** A change that stops something (end, retire, block): the button reads as one. */
  danger?: boolean;
  /** Whether the fields above hold something to send. */
  ready: boolean;
  /** Sends the change with the reason given. */
  run: (reason: string) => Promise<unknown>;
  onClose: () => void;
  /** After the server accepted it: the caller says what changed and re-reads. */
  onDone: () => void;
  wide?: boolean;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const submit = () => {
    setBusy(true);
    setFailure(null);
    run(reason.trim())
      .then(() => onDone())
      .catch((e: unknown) => setFailure(e))
      .finally(() => setBusy(false));
  };

  return (
    <Modal title={title} onClose={onClose} wide={wide} keepOnBackdropClick>
      <div className="form">
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        {children}
        <Field label={t('edit.reason')} hint={t('edit.reasonHint')}>
          <TextInput value={reason} onChange={setReason} placeholder={t('edit.reasonPlaceholder')} />
        </Field>
        <Callout tone="caution">{t('edit.lands')}</Callout>
        <div className="form-actions">
          <button
            type="button"
            className={danger ? 'danger' : 'primary'}
            disabled={busy || !ready || reason.trim() === ''}
            onClick={submit}
          >
            {busy ? t('edit.saving') : submitLabel}
          </button>
          <button type="button" className="copy" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
